import { dehydrate, QueryClient, useMutation } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { V3GenericErrorHandler } from "../../api/error-handler";
import { subscribe } from "../../api/subscribe";
import { ContactUser } from "../../api/user";
import { withGlobalPageSpinner } from "../../components/GlobalPageSpinner";
import { ContactUserFormik } from "../../components/V3ContactUser";
import { getContentInfiniteQuerie } from "../../utils/query-utils/contentInfiniteQuerie";
import { buildContentFilterQueryParams } from "../content/utils/buildContentFilterQueryParams";
import { V3CollectionPublicPageLayout } from "./CollectionDetailsPublicPageLayout";
import { getPublicUserClient, getProfileClient, getProfileServer, getPublicUserServer } from "../../utils/getAuthor";
import { getPublicCollectionClient, getShareCollectionServer } from "../../utils/query-utils/getCollection";
import { ProfileUserResponse, PublicCollectionShareStatusResponse, PublicUserResponse, ShareStatus } from "@authory-packages/core";
import { useRouter } from "next/router";
import { NextPageContext } from "next";
import { getCollectionShareStatus } from "../../api/collections";
import { getUserContent } from "../../api/content";
import { serverSideErrorResponse, statusResponse, unavailableResponse } from "../../utils/unavailableResource";
import { isHiddenAsSpam } from "../../utils/isHiddenAsSpam";
import { Errors } from "../../types/api";
import { getAuthorContentQuerie } from "../../types/queries";
import { UNAVAILABLE_REASON, UNAVAILABLE_RESOURCE_TYPE } from "../../types/unavailable_resource_type";
import { SmartSessionTracker } from "../../smart-tracking";
import { useEffect, useState } from "react";

interface V3CollectionPublicPageLayoutContainerProps {
    isStandaloneCollectionView: boolean;
    profileShareStatus?: ShareStatus;
    collectionShareStatus?: PublicCollectionShareStatusResponse;
    useCollectionSmartShareEndpoint: boolean;
    useProfileSmartShareEndpoint: boolean;
}

export const V3CollectionPublicPageLayoutContainer = ({
    isStandaloneCollectionView,
    profileShareStatus,
    collectionShareStatus,
    useCollectionSmartShareEndpoint,
    useProfileSmartShareEndpoint
}: V3CollectionPublicPageLayoutContainerProps) => {
    const router = useRouter();
    const smartShareUrl = router.query.smartShareURL as string;
    const [smartSessionTracker, setSmartSessionTracker] = useState<SmartSessionTracker | null>(null);

    // Get the author
    const { author, userSmartShareLink } = isStandaloneCollectionView
        ? getPublicUserClient()
        : getProfileClient(profileShareStatus!, useProfileSmartShareEndpoint);
    const { collection, collectionSmartShareLink } = getPublicCollectionClient(
        isStandaloneCollectionView,
        useCollectionSmartShareEndpoint,
        collectionShareStatus
    );

    const smartShareLinkToUse = userSmartShareLink || collectionSmartShareLink;

    useEffect(() => {
        if (author.isSuccess && smartShareUrl && author.data?.slug && smartShareLinkToUse?.data?.active && smartSessionTracker === null) {
            setSmartSessionTracker(new SmartSessionTracker(smartShareLinkToUse?.data?.uid, author.data?.slug));
        }
    }, [author.isSuccess]);

    // Get the articles
    const searchArgs = { collection: useCollectionSmartShareEndpoint ? smartShareUrl : (collection.data?.canonicalSlug as string) };
    const params = buildContentFilterQueryParams(searchArgs, true, undefined, true);

    const articles = getContentInfiniteQuerie(null, author?.data?.slug, params, collection.isFetched && collection.isSuccess);

    // Contact User Mutation
    const contactUserMutation = useMutation({
        mutationFn: ContactUser,
        onError: V3GenericErrorHandler(toast.error)
    });

    const onContactUserSubmit = async (data: ContactUserFormik) => {
        if (!author.data) return;

        await contactUserMutation.mutateAsync({ userSlug: author.data?.slug, data });
    };

    const subcribeMutation = useMutation({
        mutationFn: subscribe,
        onError: V3GenericErrorHandler(toast.error)
    });

    const onSubscribeFormSubmit = async (email: string) => {
        if (!author.data) return;
        await subcribeMutation.mutateAsync({ email, userId: author.data?.slug });
    };

    return (
        <>
            {withGlobalPageSpinner(
                author.isLoading,
                <>
                    {collection.data && (
                        <V3CollectionPublicPageLayout
                            items={articles.data?.pages || []}
                            collection={collection.data}
                            itemsLoading={articles.isFetching}
                            contentCanFetchmore={articles.hasNextPage || false}
                            contentLoadmore={articles.fetchNextPage}
                            author={author.data!}
                            onContactUserSubmit={onContactUserSubmit}
                            rules={[]}
                            isStandaloneCollectionView={isStandaloneCollectionView}
                            onSubscribeFormSubmit={onSubscribeFormSubmit}
                            smartShareUrl={smartShareUrl}
                            onItemClick={(id) => {
                                smartSessionTracker && smartSessionTracker.trackArticle(id);
                            }}
                        />
                    )}
                </>
            )}
        </>
    );
};

export const getServerSidePropsWithCollection = (isSmartSharePage: boolean, isProfilePage: boolean) => async (ctx: NextPageContext) => {
    const queryClient = new QueryClient();
    const smartShareUrl = ctx.query.smartShareURL as string;
    let author: ProfileUserResponse | PublicUserResponse | undefined = undefined;

    try {
        const authorSlug = ctx.query.author as string;

        // Get public user first
        const publicUser = await getPublicUserServer(queryClient, ctx);
        author = publicUser;

        if (!publicUser) return unavailableResponse(ctx, queryClient, UNAVAILABLE_RESOURCE_TYPE.COLLECTION, UNAVAILABLE_REASON.NOT_FOUND);
        // A plain 404 with nothing dehydrated, so the page html carries none of the account's data
        if (isHiddenAsSpam(publicUser)) return statusResponse(ctx, Errors.NotFound);

        // Collection accessed via profile page
        if (isProfilePage) {
            const isNotShared = publicUser.profileShareStatus === ShareStatus.Unpublished;

            if (!isSmartSharePage && isNotShared) {
                return unavailableResponse(ctx, queryClient, UNAVAILABLE_RESOURCE_TYPE.PROFILE, UNAVAILABLE_REASON.NOT_SHARED);
            }

            // -> collection profile page
            // -> smart share collection profile page
            const fetchProfileOnSSR = publicUser.profileShareStatus === ShareStatus.Published || isSmartSharePage;
            if (fetchProfileOnSSR) {
                const profileServerRes = await getProfileServer(queryClient, ctx, isSmartSharePage);

                author = profileServerRes.author;

                if (isSmartSharePage && profileServerRes.smartShareLink && !profileServerRes.smartShareLink.active) {
                    return unavailableResponse(ctx, queryClient, UNAVAILABLE_RESOURCE_TYPE.PROFILE, UNAVAILABLE_REASON.NOT_SHARED);
                }
            }
        }

        // Otherwise validate collection share if not smart share page
        const collectionAccessKey = ctx.query.collectionAccessKey as string;
        let collectionShareStatus: PublicCollectionShareStatusResponse | null = null;

        if (!isSmartSharePage && !isProfilePage) {
            collectionShareStatus = await getCollectionShareStatus(authorSlug, collectionAccessKey);

            if (collectionShareStatus.shareStatus === ShareStatus.Unpublished) {
                return unavailableResponse(ctx, queryClient, UNAVAILABLE_RESOURCE_TYPE.COLLECTION, UNAVAILABLE_REASON.NOT_SHARED);
            }
        }

        // Get articles
        const fetchCollectionDataOnSSR = (!!collectionShareStatus && !collectionShareStatus.isPasswordProtected) || isSmartSharePage || isProfilePage;

        if (fetchCollectionDataOnSSR) {
            const collServerRes = await getShareCollectionServer(queryClient, ctx, isSmartSharePage && !isProfilePage);

            if (!isProfilePage && !!collServerRes.smartShareLink && !collServerRes.smartShareLink.active) {
                return unavailableResponse(ctx, queryClient, UNAVAILABLE_RESOURCE_TYPE.COLLECTION, UNAVAILABLE_REASON.NOT_SHARED);
            }

            const searchArgs = { collection: isSmartSharePage && !isProfilePage ? smartShareUrl : collServerRes.col.canonicalSlug };
            const params = buildContentFilterQueryParams(searchArgs, true, undefined, true);

            const articleQuery = getAuthorContentQuerie(null, authorSlug, params);

            await queryClient.prefetchInfiniteQuery({
                queryKey: articleQuery,
                queryFn: () => getUserContent(null, authorSlug, params),
                initialPageParam: 1
            });
        }

        return {
            props: {
                dehydratedState: dehydrate(queryClient),
                author,
                profileShareStatus: publicUser?.profileShareStatus,
                collectionAccessKey: collectionAccessKey || null,
                collectionShareStatus
            }
        };
    } catch (error) {
        return serverSideErrorResponse(ctx, queryClient, UNAVAILABLE_RESOURCE_TYPE.COLLECTION, error);
    }
};
